import {
  discoveryOfferKey,
  rankDiscoveryFeed,
  type DiscoveryFeedOptions,
} from "./feed-ranking";
import type { ProductView } from "./types";

export const DISCOVERY_HISTORY_KEY = "kiloprice.discovery-history.v2";
const LEGACY_HISTORY_KEY = "kiloprice.discovery-history.v1";
const HISTORY_TTL_MS = 7 * 24 * 60 * 60 * 1_000;
const MAX_VISITS = 6;
const MAX_HISTORY_LENGTH = 12;

interface VisitHistory {
  at: number;
  keys: string[];
}

interface FeedStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

interface FeedVisitOptions {
  seed: number;
  now?: () => number;
  storage?: FeedStorage;
}

function readHistory(storage: FeedStorage | undefined, now: number): VisitHistory[] {
  try {
    const raw = storage?.getItem(DISCOVERY_HISTORY_KEY) ?? storage?.getItem(LEGACY_HISTORY_KEY);
    if (!raw || raw.length > 48_000) return [];
    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== "object") return [];
    const saved = value as { visits?: unknown };
    const entries: unknown[] = Array.isArray(saved.visits) ? saved.visits.slice(0, MAX_VISITS) : [value];
    return entries.flatMap((entry): VisitHistory[] => {
      if (!entry || typeof entry !== "object") return [];
      const record = entry as { at?: unknown; keys?: unknown };
      if (typeof record.at !== "number" || !Number.isFinite(record.at) || record.at > now ||
        now - record.at > HISTORY_TTL_MS || !Array.isArray(record.keys)) return [];
      const keys = [...new Set(record.keys.filter(
        (key): key is string => typeof key === "string" && key.length > 0 && key.length <= 512,
      ))].slice(0, MAX_HISTORY_LENGTH);
      return keys.length ? [{ at: record.at, keys }] : [];
    });
  } catch {
    return [];
  }
}

/** Stable until a fresh document or an explicit, safe home-feed resume. */
export function createDiscoveryFeedVisit({
  seed,
  now = Date.now,
  storage,
}: FeedVisitOptions) {
  let priorVisits = readHistory(storage, now());
  let currentHistory: VisitHistory | undefined;
  let visitSeed = seed;
  let cachedRevision: string | undefined;
  let cachedFeed: ProductView[] | undefined;

  return {
    /** The lifecycle gate, never render/pagination/filter handlers, owns this. */
    renew(nextSeed: number): void {
      if (currentHistory) priorVisits = [currentHistory, ...priorVisits].slice(0, MAX_VISITS);
      priorVisits = priorVisits.filter((visit) => now() >= visit.at && now() - visit.at <= HISTORY_TTL_MS);
      currentHistory = undefined;
      visitSeed = nextSeed;
      cachedRevision = undefined;
      cachedFeed = undefined;
    },
    rank(
      products: readonly ProductView[],
      revision: string,
      options: DiscoveryFeedOptions,
    ): ProductView[] {
      if (cachedFeed && cachedRevision === revision) {
        // Freeze the order for the visit, not the ProductView objects. Locale
        // assets deliberately share the same catalog timestamp and offer keys,
        // so returning the old objects here would leave card titles in the
        // previous language until the Mini App was restarted.
        const currentByKey = new Map(
          products.map((product) => [discoveryOfferKey(product), product]),
        );
        if (
          currentByKey.size === products.length &&
          cachedFeed.length === products.length
        ) {
          const remapped = cachedFeed.flatMap((product): ProductView[] => {
            const current = currentByKey.get(discoveryOfferKey(product));
            return current ? [current] : [];
          });
          if (remapped.length === products.length) {
            if (
              remapped.some((product, index) => product !== cachedFeed![index])
            ) {
              cachedFeed = remapped;
            }
            return cachedFeed;
          }
        }
      }
      // Data/date revisions invalidate stale offers, but ordinary UI updates and
      // delayed preference sync do not jump the feed the user is already reading.
      cachedFeed = rankDiscoveryFeed(products, {
        ...options,
        visitSeed,
        recentVisits: priorVisits.map((visit) => visit.keys),
      });
      cachedRevision = revision;
      // A bounded history of selected home prefixes, not proof that every card
      // was seen. No Telegram identifier, query or new server analytics record.
      currentHistory = {
        at: now(), keys: cachedFeed.slice(0, MAX_HISTORY_LENGTH).map(discoveryOfferKey),
      };
      try {
        storage?.setItem(DISCOVERY_HISTORY_KEY, JSON.stringify({
          visits: [currentHistory, ...priorVisits].slice(0, MAX_VISITS),
        }));
      } catch {
        // Storage is optional; the in-memory visit remains stable.
      }
      return cachedFeed;
    },
  };
}

export type DiscoveryLifecycleSource = "telegram" | "document";

/** Telegram may keep a minimized WebView alive. A safe top-of-home return may
 * start a fresh visit, but Back, a product dialog, typing and mid-feed reading
 * never do. Duplicate document/Telegram activation events consume one boundary.
 */
export function createDiscoveryFeedLifecycle(now: () => number = Date.now) {
  let inactive: { at: number; safeHome: boolean; telegram: boolean } | undefined;
  return {
    deactivate(safeHome: boolean, source: DiscoveryLifecycleSource): void {
      if (inactive) {
        inactive.safeHome &&= safeHome;
        inactive.telegram ||= source === "telegram";
      } else inactive = { at: now(), safeHome, telegram: source === "telegram" };
    },
    activate(safeHome: boolean, source: DiscoveryLifecycleSource): boolean {
      if (!inactive) return false;
      const boundary = inactive;
      inactive = undefined;
      // A native minimized/reopened Mini App is a stronger visit signal than
      // switching browser tabs. Thresholds are UX choices, not platform facts.
      const minimumAbsence = boundary.telegram || source === "telegram" ? 2_000 : 5 * 60_000;
      return boundary.safeHome && safeHome && now() - boundary.at >= minimumAbsence;
    },
  };
}
